import React, { useState, useRef, useEffect, useMemo } from 'react';
import LookPreviewCanvas from '../shared/LookPreviewCanvas';
import { deleteLook } from '../shared/api';
import { FullPageSpinner } from '../shared/Spinner';

// Lazy-load wrapper — only renders children when scrolled into view
const LazyRender = ({ children, height }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' } // start loading 200px before scrolling into view
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (visible) return <>{children}</>;
  return <div ref={ref} style={{ minHeight: height, background: '#f5f5f5', borderRadius: 4 }} />;
};

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'season', label: 'Season' },
  { value: 'purchases_desc', label: 'Purchases (high-low)' },
  { value: 'purchases_asc', label: 'Purchases (low-high)' },
  { value: 'updated', label: 'Recently Updated' },
];

const ManageLooks = ({ looks, loading, onEdit, onDuplicate, onDeleted, onNew, purchaseCounts }) => {
  const [filter, setFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [deleting, setDeleting] = useState(null);

  const getPurchases = (look) => (purchaseCounts || {})[look.id] || 0;

  const seasons = useMemo(
    () => [...new Set(looks.map(l => l.season).filter(Boolean))].sort(),
    [looks]
  );

  const sorted = useMemo(() => {
    let list = looks;
    if (filter || seasonFilter) {
      list = looks.filter(l => {
        if (filter && !l.name.toLowerCase().includes(filter.toLowerCase())) return false;
        if (seasonFilter && l.season !== seasonFilter) return false;
        return true;
      });
    }
    return [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name': return (a.name || '').localeCompare(b.name || '');
        case 'season': return (a.season || '').localeCompare(b.season || '') || (a.name || '').localeCompare(b.name || '');
        case 'purchases_desc': return getPurchases(b) - getPurchases(a);
        case 'purchases_asc': return getPurchases(a) - getPurchases(b);
        case 'updated': return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
        default: return 0;
      }
    });
  }, [looks, filter, seasonFilter, sortBy, purchaseCounts]);

  const handleDelete = async (look) => {
    if (!confirm(`Delete "${look.name}"? This cannot be undone.`)) return;
    setDeleting(look.id);
    try {
      await deleteLook(look.id);
      onDeleted(look.id);
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.errors?.join(', ') || err.message));
    }
    setDeleting(null);
  };

  if (loading) {
    return <FullPageSpinner label="Loading looks..." />;
  }

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search by name..."
          style={{
            padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4,
            fontSize: 13, width: 250
          }}
        />
        <select
          value={seasonFilter}
          onChange={e => setSeasonFilter(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4,
            fontSize: 13, background: '#fff'
          }}
        >
          <option value="">All seasons</option>
          {seasons.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4,
            fontSize: 13, background: '#fff'
          }}
        >
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: '#666' }}>
          {sorted.length} look{sorted.length !== 1 ? 's' : ''}
        </span>
      </div>

      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 16, color: '#999', marginBottom: 12 }}>
            {looks.length === 0 ? 'No look templates yet' : 'No looks match your filter'}
          </div>
          <button
            onClick={onNew}
            style={{
              padding: '10px 24px', background: '#1976d2', color: '#fff',
              border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14
            }}
          >
            Create First Look
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 16
        }}>
          {sorted.map(look => {
            const aspect = (look.canvas_width || 2400) / (look.canvas_height || 3000);
            const thumbW = aspect >= 1 ? 200 : Math.round(160 * aspect);
            const thumbH = aspect >= 1 ? Math.round(200 / aspect) : 200;

            return (
              <div
                key={look.id}
                style={{
                  border: '1px solid #e0e0e0', borderRadius: 6,
                  background: '#fff', overflow: 'hidden',
                  transition: 'box-shadow .15s',
                  cursor: 'pointer'
                }}
                onClick={() => onEdit(look)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.12)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                {/* Large preview thumbnail — lazy loaded */}
                <div style={{
                  background: '#f0f0f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 8, minHeight: 160
                }}>
                  <LazyRender height={thumbH}>
                    <LookPreviewCanvas
                      look={look}
                      width={thumbW}
                      height={thumbH}
                      allLooks={looks}
                    />
                  </LazyRender>
                </div>

                {/* Look info */}
                <div style={{ padding: '8px 12px' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {look.name}
                    {look.look_type === 'collage' && (
                      <span style={{
                        fontSize: 9, color: '#fff', background: '#9c27b0',
                        borderRadius: 2, padding: '1px 4px', fontWeight: 500
                      }}>Collage</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                    {look.season || 'no season'} | {look.pose_number || 'no pose'} | {look.canvas_width}x{look.canvas_height}
                    {(look.text_fields || []).length > 0 && ` | ${(look.text_fields || []).length} text`}
                  </div>
                  {getPurchases(look) > 0 && (
                    <div style={{
                      fontSize: 10, color: '#2e7d32', fontWeight: 600, marginTop: 2
                    }}>
                      {getPurchases(look)} purchase{getPurchases(look) !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{
                  display: 'flex', borderTop: '1px solid #f0f0f0', background: '#fafafa'
                }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(look); }}
                    style={{
                      flex: 1, padding: '8px', border: 'none', background: 'none',
                      fontSize: 12, color: '#1976d2', cursor: 'pointer', fontWeight: 500
                    }}
                  >
                    Edit
                  </button>
                  {onDuplicate && (
                    <>
                      <div style={{ width: 1, background: '#e0e0e0' }} />
                      <button
                        onClick={(e) => { e.stopPropagation(); onDuplicate(look); }}
                        style={{
                          flex: 1, padding: '8px', border: 'none', background: 'none',
                          fontSize: 12, color: '#00695c', cursor: 'pointer', fontWeight: 500
                        }}
                      >
                        Duplicate
                      </button>
                    </>
                  )}
                  <div style={{ width: 1, background: '#e0e0e0' }} />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(look); }}
                    disabled={deleting === look.id}
                    style={{
                      flex: 1, padding: '8px', border: 'none', background: 'none',
                      fontSize: 12, color: '#d32f2f', cursor: 'pointer', fontWeight: 500
                    }}
                  >
                    {deleting === look.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManageLooks;
